import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Table, Tooltip } from '@mantine/core';
import { ChevronDown, ChevronUp } from 'tabler-icons-react';
import AppStack from '../../common/AppStack';
import ResponsiveModal from '../../common/ResponsiveModal';
import { getVariantOptions } from '../../../helpers/webstoreHelper';
import AppText from '../../common/AppText';
import AppFlexbox from '../../common/AppFlexbox';
import AppCheckbox from '../../common/AppCheckbox';
import * as classes from '../../../styles/nestedStyles.module.css';
import WsColorSwatch from '../webstore/blocks/variantPicker/WsColorSwatch';

const PrintDesignEditorVariantSelectModal = ({
  isOpen,
  onClose,
  ecomExternalProduct,
  onConfirm,
  variantDesigns,
  selectedVariantDesign,
  removedVariantIds
}) => {
  const [formState, setFormState] = useState({
    activeTab: null,
    expandedVariantOptionValues: [],
    variantIds: []
  });

  const variantOptions = getVariantOptions(
    ecomExternalProduct?.ecomVendorProductVariants || [],
    { beginningColor: 'white' }
  );
  const colorOption = variantOptions.find(
    (op) => op.name.toLowerCase() === 'color'
  );

  const selectedVariantOption = variantOptions.find(
    (v) => v.name === formState.activeTab
  );
  const notSelectedVariantOptions = variantOptions.filter(
    (f) => f.name !== selectedVariantOption?.name
  );

  const disabledVariantIds =
    variantDesigns
      ?.filter((d) => d.id !== selectedVariantDesign?.id)
      .flatMap((vd) => vd.variantIds) || [];

  const allOptionsDisabled =
    disabledVariantIds.length ===
    ecomExternalProduct?.ecomVendorProductVariants?.length;

  const allVariantsSelected =
    ecomExternalProduct &&
    formState.variantIds.length > 0 &&
    formState.variantIds.length ===
      ecomExternalProduct.ecomVendorProductVariants.length -
        disabledVariantIds.length;

  useEffect(() => {
    if (isOpen) {
      setFormState({
        ...formState,
        activeTab: colorOption?.name || variantOptions[0]?.name || null,
        expandedVariantOptionValues: [],
        variantIds: selectedVariantDesign?.isMainDesign
          ? ecomExternalProduct.ecomVendorProductVariants
              .map((v) => v.uuid)
              .filter(
                (uuid) =>
                  !disabledVariantIds.includes(uuid) &&
                  !removedVariantIds?.includes(uuid)
              )
          : selectedVariantDesign?.variantIds || []
      });
    }
  }, [isOpen]);

  return (
    <ResponsiveModal
      formSectionProps={{
        onCancel: onClose,
        onSubmit: () => {
          onConfirm(formState.variantIds);
          onClose();
        },
        submitColor: 'dark',
        submitTitle: 'Continue',
        isSubmitDisabled: formState.variantIds.length === 0,
        cancelTitle: 'Cancel'
      }}
      onClose={onClose}
      opened={isOpen}
      size={800}
      title="Select design variants"
    >
      <AppStack>
        <AppText
          style={{
            fontSize: 14,
            color: '#666',
            fontStyle: 'italic',
            textAlign: 'center'
          }}
        >
          Select the variants you want to design
        </AppText>

        <AppFlexbox
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <AppFlexbox>
            <AppText style={{ fontSize: 14, color: '#666' }}>
              {formState.variantIds.length} of{' '}
              {ecomExternalProduct?.ecomVendorProductVariants?.length || 0}{' '}
              variants selected
            </AppText>
          </AppFlexbox>

          <Button.Group>
            {variantOptions.map((v) => (
              <Button
                key={v.name}
                color="dark"
                onClick={() => {
                  setFormState((c) => ({
                    ...c,
                    activeTab: v.name,
                    expandedVariantOptionValues: []
                  }));
                }}
                variant={formState.activeTab === v.name ? 'filled' : 'outline'}
              >
                {v.name}
              </Button>
            ))}
          </Button.Group>
        </AppFlexbox>

        <Table
          horizontalSpacing={0}
          style={{
            borderTop: 'solid 1px  #dee2e6',
            width: '100%'
          }}
          verticalSpacing={0}
          withTableBorder
        >
          <Table.Thead style={{ backgroundColor: '#E1E1E1' }}>
            <Table.Tr>
              <Table.Th>
                <AppFlexbox
                  style={{ padding: 8, gap: 8, alignItems: 'center' }}
                >
                  <Tooltip
                    disabled={!allOptionsDisabled}
                    label="All variants assigned to a design"
                    position="bottom"
                    withArrow
                    withinPortal
                  >
                    <AppCheckbox
                      checked={allVariantsSelected}
                      disabled={allOptionsDisabled}
                      indeterminate={
                        !allVariantsSelected && formState.variantIds.length > 0
                      }
                      onChange={() => {
                        setFormState((c) => {
                          const newVariantIds = allVariantsSelected
                            ? []
                            : ecomExternalProduct?.ecomVendorProductVariants
                                ?.map((v) => v.uuid)
                                .filter(
                                  (uuid) => !disabledVariantIds.includes(uuid)
                                ) || [];
                          return {
                            ...c,
                            variantIds: newVariantIds
                          };
                        });
                      }}
                    />
                  </Tooltip>

                  <AppText
                    style={{
                      whiteSpace: 'nowrap',
                      fontSize: 14,
                      fontWeight: 500
                    }}
                  >
                    {selectedVariantOption?.name || 'Option'}
                  </AppText>
                </AppFlexbox>
              </Table.Th>

              {notSelectedVariantOptions.map((op) => (
                <Table.Th key={op.name}>
                  <AppFlexbox
                    style={{ padding: 8, gap: 8, alignItems: 'center' }}
                  >
                    <AppText
                      style={{
                        whiteSpace: 'nowrap',
                        fontSize: 14,
                        fontWeight: 500,
                        textAlign: 'end'
                      }}
                    >
                      {op.name}
                    </AppText>
                  </AppFlexbox>
                </Table.Th>
              ))}
              <Table.Th>
                <AppFlexbox
                  style={{ padding: 8, gap: 8, alignItems: 'center' }}
                >
                  <AppText
                    style={{
                      whiteSpace: 'nowrap',
                      fontSize: 14,
                      fontWeight: 500,
                      textAlign: 'end'
                    }}
                  />
                </AppFlexbox>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {!selectedVariantOption ? (
              <Table.Tr>
                <Table.Td colSpan={2}>
                  <AppFlexbox
                    style={{
                      padding: 20,
                      alignItems: 'center',
                      justifyContent: 'center',
                      textAlign: 'center'
                    }}
                  >
                    <AppText
                      style={{ fontSize: 14, color: '#666', fontWeight: 500 }}
                    >
                      Please select a variant option to see the available
                      variants
                    </AppText>
                  </AppFlexbox>
                </Table.Td>
              </Table.Tr>
            ) : (
              selectedVariantOption?.values.map((op) => {
                const variants = ecomExternalProduct?.ecomVendorProductVariants?.filter(
                  (v) =>
                    v.ecomVendorProductVariantOptions?.some(
                      (o) =>
                        o.name === selectedVariantOption.name &&
                        o.value === op.value
                    )
                );

                const isExpanded = formState.expandedVariantOptionValues.includes(
                  op.value
                );

                const hasAnyOptionVariantSelected = variants.some((v) =>
                  formState.variantIds.includes(v.uuid)
                );

                const allOptionVariantsSelected =
                  hasAnyOptionVariantSelected &&
                  variants.every(
                    (v) =>
                      disabledVariantIds.includes(v.uuid) ||
                      formState.variantIds.includes(v.uuid)
                  );

                const allOptionVariantsDisabled = variants.every((v) =>
                  disabledVariantIds.includes(v.uuid)
                );

                return (
                  <React.Fragment key={op.value}>
                    <Table.Tr
                      className={classes['hover-action-card']}
                      onClick={() => {
                        setFormState({
                          ...formState,
                          expandedVariantOptionValues: isExpanded
                            ? formState.expandedVariantOptionValues.filter(
                                (v) => v !== op.value
                              )
                            : [
                                ...formState.expandedVariantOptionValues,
                                op.value
                              ]
                        });
                      }}
                    >
                      <Table.Td>
                        <AppFlexbox
                          style={{
                            padding: '12px 8px',
                            gap: 8,
                            alignItems: 'center'
                          }}
                        >
                          <Tooltip
                            disabled={!allOptionVariantsDisabled}
                            label="All variants for this option are already assigned to a design"
                            position="bottom"
                            withArrow
                            withinPortal
                          >
                            <AppCheckbox
                              checked={allOptionVariantsSelected}
                              disabled={allOptionVariantsDisabled}
                              indeterminate={
                                !allOptionVariantsSelected &&
                                hasAnyOptionVariantSelected
                              }
                              onChange={(e) => {
                                setFormState((c) => {
                                  const newVariantIds = allOptionVariantsSelected
                                    ? c.variantIds.filter(
                                        (id) =>
                                          !variants.some((v) => v.uuid === id)
                                      )
                                    : [
                                        ...c.variantIds,
                                        ...variants
                                          .filter(
                                            (v) =>
                                              !c.variantIds.includes(v.uuid) &&
                                              !disabledVariantIds.includes(
                                                v.uuid
                                              )
                                          )
                                          .map((v) => v.uuid)
                                      ];

                                  return {
                                    ...c,
                                    variantIds: newVariantIds
                                  };
                                });
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            />
                          </Tooltip>
                          {selectedVariantOption.name.toLowerCase() ===
                            'color' && (
                            <WsColorSwatch
                              primaryColor={op.primaryColor}
                              secondaryColor={op.secondaryColor}
                              size={20}
                            />
                          )}

                          <AppText
                            style={{
                              whiteSpace: 'nowrap',
                              fontSize: 14
                            }}
                          >
                            {op.value}
                          </AppText>
                        </AppFlexbox>
                      </Table.Td>

                      {notSelectedVariantOptions.map((vop) => (
                        <Table.Td key={vop.name}>
                          <AppFlexbox
                            style={{
                              padding: '12px 8px',
                              flex: 1,
                              color: '#666'
                            }}
                          >
                            {
                              variants.filter((v) =>
                                v.ecomVendorProductVariantOptions?.some(
                                  (o) => o.name === vop.name
                                )
                              ).length
                            }{' '}
                            options
                          </AppFlexbox>
                        </Table.Td>
                      ))}

                      <Table.Td>
                        <AppFlexbox
                          style={{
                            padding: '12px 8px',
                            flex: 1,
                            justifyContent: 'end',
                            color: '#000',
                            textDecoration: 'none'
                          }}
                        >
                          {isExpanded ? (
                            <ChevronUp size={18} />
                          ) : (
                            <ChevronDown size={18} />
                          )}
                        </AppFlexbox>
                      </Table.Td>
                    </Table.Tr>

                    {isExpanded &&
                      variants.map((v, index) => {
                        const variantSelected = formState.variantIds.includes(
                          v.uuid
                        );

                        const variantDisabled = disabledVariantIds.includes(
                          v.uuid
                        );

                        return (
                          <Table.Tr
                            key={v.uuid}
                            style={
                              index === variants.length - 1
                                ? {}
                                : { border: 'none' }
                            }
                          >
                            <Table.Td style={{ border: 'none' }}>
                              <AppFlexbox
                                style={{
                                  padding: 8,
                                  gap: 8,
                                  alignItems: 'center'
                                }}
                              >
                                <Tooltip
                                  disabled={!variantDisabled}
                                  label="Variant is already assigned to a design"
                                  position="bottom"
                                  withArrow
                                  withinPortal
                                >
                                  <AppCheckbox
                                    checked={variantSelected}
                                    disabled={variantDisabled}
                                    onChange={(e) => {
                                      setFormState((c) => ({
                                        ...c,
                                        variantIds: variantSelected
                                          ? c.variantIds.filter(
                                              (id) => id !== v.uuid
                                            )
                                          : [...c.variantIds, v.uuid]
                                      }));
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  />
                                </Tooltip>
                              </AppFlexbox>
                            </Table.Td>

                            {notSelectedVariantOptions.map((vop) => {
                              const variantOption = v.ecomVendorProductVariantOptions?.find(
                                (o) => o.name === vop.name
                              );

                              return (
                                <Table.Td key={vop.name}>
                                  <AppFlexbox
                                    style={{
                                      padding: 8,
                                      gap: 8,
                                      flex: 1,
                                      alignItems: 'center'
                                    }}
                                  >
                                    {vop.name.toLowerCase() === 'color' && (
                                      <WsColorSwatch
                                        primaryColor={v.primaryColor}
                                        secondaryColor={v.secondaryColor}
                                        size={20}
                                      />
                                    )}
                                    <AppText style={{ fontSize: 14 }}>
                                      {variantOption?.value || '-'}
                                    </AppText>
                                  </AppFlexbox>
                                </Table.Td>
                              );
                            })}

                            <Table.Td style={{ border: 'none' }}>
                              <AppFlexbox
                                style={{
                                  padding: 8,
                                  flex: 1,
                                  justifyContent: 'end',
                                  color: '#000',
                                  textDecoration: 'none'
                                }}
                              >
                                <></>
                              </AppFlexbox>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                  </React.Fragment>
                );
              })
            )}
          </Table.Tbody>
        </Table>
      </AppStack>
    </ResponsiveModal>
  );
};

PrintDesignEditorVariantSelectModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  ecomExternalProduct: PropTypes.object,
  isOpen: PropTypes.bool,
  removedVariantIds: PropTypes.array,
  selectedVariantDesign: PropTypes.object,
  variantDesigns: PropTypes.array
};

export default PrintDesignEditorVariantSelectModal;
